import { useState } from "react";
import { Link } from "react-router-dom";
import { Reply, Edit2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { VoteButtons } from "./VoteButtons";
import { MembershipBadge } from "@/components/MembershipBadge";
import { MediaUploader, MediaPreview } from "@/components/MediaUploader";
import { toast } from "sonner";

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    if (s < 604800) return `${Math.floor(s/86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

function Avatar({ url, name, size = 24 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.45 }}>
            {initial}
        </div>
    );
}

// Single non-recursive node. Children are rendered by the parent CommentTree component.
function CommentNode({ comment, targetType, targetId, onReply, onUpdated, depth }) {
    const { user } = useAuth();
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyBody, setReplyBody] = useState("");
    const [replyMedia, setReplyMedia] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState(comment.body);
    const [busy, setBusy] = useState(false);
    const [deleted, setDeleted] = useState(!!comment.deleted_at);

    const isOwn = user && user.id === comment.user_id;
    const isAdmin = user && user.role === "admin";

    async function submitReply() {
        if (!replyBody.trim() && !replyMedia) return;
        setBusy(true);
        try {
            const payload = {
                target_type: targetType, target_id: targetId, parent_id: comment.id,
                body: replyBody.trim() || "(attached media)",
            };
            if (replyMedia) payload.media_id = replyMedia.id;
            const { data } = await api.post("/social/comments", payload);
            onReply && onReply(data);
            setReplyBody(""); setReplyMedia(null); setReplyOpen(false);
        } catch (e) { toast.error("Reply failed"); }
        finally { setBusy(false); }
    }

    async function submitEdit() {
        if (!editBody.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.patch(`/social/comments/${comment.id}`, { body: editBody.trim() });
            onUpdated && onUpdated(data);
            setEditing(false);
        } catch (e) { toast.error("Edit failed"); }
        finally { setBusy(false); }
    }

    async function doDelete() {
        if (!window.confirm("Delete this comment?")) return;
        setBusy(true);
        try {
            await api.delete(`/social/comments/${comment.id}`);
            setDeleted(true);
            onUpdated && onUpdated({ ...comment, deleted_at: new Date().toISOString(), body: "[deleted]" });
        } catch (e) { toast.error("Delete failed"); }
        finally { setBusy(false); }
    }

    const isCouncil = comment.user_subscription_tier === "research_council";
    const isWitness = comment.user_subscription_tier === "verified_witness";
    const elevated = isCouncil; // Council comments get the gold elevated container

    return (
        <div
            className={`flex gap-3 ${elevated ? "ok-card p-3 border-l-2" : ""}`}
            style={elevated ? { borderLeftColor: "#e7c873", background: "rgba(231,200,115,0.04)" } : undefined}
            data-testid={`comment-${comment.id}`}
        >
            <div className="flex flex-col items-center flex-shrink-0 pt-1">
                <VoteButtons targetType="comment" targetId={comment.id} initialScore={comment.score || 0} initialMyVote={comment.my_vote || 0} layout="vertical" size="sm" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs flex-wrap">
                    <Avatar url={comment.user_avatar_url} name={comment.user_name} size={24} />
                    <Link to={`/u/${comment.user_id}`} className="font-mono uppercase tracking-wide hover:text-[var(--ok-accent)] truncate" data-testid={`comment-author-${comment.id}`}>
                        {comment.user_name || "Witness"}
                    </Link>
                    {isCouncil && (
                        <span className="inline-flex items-center gap-1" title="Research Council · Trusted Source" data-testid={`comment-badge-council-${comment.id}`}>
                            <MembershipBadge tier="research_council" size="sm" />
                            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: "#e7c873" }}>Council</span>
                        </span>
                    )}
                    {isWitness && (
                        <span className="inline-flex items-center gap-1" title="Verified Witness" data-testid={`comment-badge-witness-${comment.id}`}>
                            <MembershipBadge tier="verified_witness" size="sm" />
                            <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">Witness</span>
                        </span>
                    )}
                    <span className="opacity-40">·</span>
                    <span className="font-mono opacity-60">{timeAgo(comment.created_at)}{comment.edited_at ? " · edited" : ""}</span>
                </div>
                {deleted ? (
                    <div className="mt-2 opacity-40 italic text-sm">[deleted]</div>
                ) : editing ? (
                    <div className="mt-2">
                        <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className="ok-input w-full text-sm" rows={3} data-testid={`comment-edit-textarea-${comment.id}`} />
                        <div className="mt-2 flex gap-2">
                            <button onClick={submitEdit} disabled={busy} className="ok-btn-primary text-xs">Save</button>
                            <button onClick={() => setEditing(false)} className="ok-btn-outline text-xs">Cancel</button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words">{comment.body}</div>
                )}

                {!deleted && comment.media && (
                    <div className="mt-2" data-testid={`comment-media-${comment.id}`}>
                        <MediaPreview media={comment.media} />
                    </div>
                )}

                {!deleted && (
                    <div className="mt-2 flex items-center gap-3 text-xs opacity-70">
                        <button onClick={() => setReplyOpen((v) => !v)} className="hover:text-[var(--ok-accent)] flex items-center gap-1" data-testid={`comment-reply-btn-${comment.id}`}>
                            <Reply className="w-3 h-3" /> Reply
                        </button>
                        {isOwn && !editing && (
                            <button onClick={() => setEditing(true)} className="hover:text-[var(--ok-accent)] flex items-center gap-1">
                                <Edit2 className="w-3 h-3" /> Edit
                            </button>
                        )}
                        {(isOwn || isAdmin) && (
                            <button onClick={doDelete} disabled={busy} className="hover:text-red-400 flex items-center gap-1">
                                <Trash2 className="w-3 h-3" /> Delete
                            </button>
                        )}
                    </div>
                )}

                {replyOpen && (
                    <div className="mt-3">
                        <textarea
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            placeholder="Write a reply…"
                            rows={2}
                            className="ok-input w-full text-sm"
                            data-testid={`comment-reply-textarea-${comment.id}`}
                        />
                        {replyMedia && (
                            <div className="mt-2" data-testid={`comment-reply-media-${comment.id}`}>
                                <MediaPreview media={replyMedia} onRemove={() => setReplyMedia(null)} />
                            </div>
                        )}
                        <div className="mt-2 flex gap-2 items-center flex-wrap">
                            <button onClick={submitReply} disabled={busy || (!replyBody.trim() && !replyMedia)} className="ok-btn-primary text-xs" data-testid={`comment-reply-submit-${comment.id}`}>
                                Post reply
                            </button>
                            <button onClick={() => { setReplyOpen(false); setReplyBody(""); setReplyMedia(null); }} className="ok-btn-outline text-xs">Cancel</button>
                            <MediaUploader compact onUploaded={setReplyMedia} testId={`comment-reply-attach-${comment.id}`} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Builds and renders the whole tree iteratively (no component self-recursion → babel-safe).
function flattenTree(allComments) {
    const byParent = new Map();
    for (const c of allComments) {
        const p = c.parent_id || null;
        if (!byParent.has(p)) byParent.set(p, []);
        byParent.get(p).push(c);
    }
    const result = [];
    const walk = (parentId, depth) => {
        const list = byParent.get(parentId) || [];
        for (const c of list) {
            result.push({ comment: c, depth });
            walk(c.id, depth + 1);
        }
    };
    walk(null, 0);
    return result;
}

export function CommentTree({ items, targetType, targetId, onReply, onUpdated }) {
    const flat = flattenTree(items);
    return (
        <div className="space-y-6" data-testid="comment-tree">
            {flat.map(({ comment, depth }) => (
                <div key={comment.id} style={{ paddingLeft: depth > 0 ? Math.min(depth, 6) * 18 : 0, borderLeft: depth > 0 ? "1px solid var(--ok-border)" : "none", marginLeft: depth > 0 ? 4 : 0 }}>
                    <CommentNode comment={comment} targetType={targetType} targetId={targetId} onReply={onReply} onUpdated={onUpdated} depth={depth} />
                </div>
            ))}
        </div>
    );
}
